import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Swiss Chem International',
  
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  landline: '041-2611464',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, '-');
};

const fmtQty = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};

export const generateRecipePdf = (formula) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 12;
  let y = 14;

  const totalInput = parseFloat(formula.total_input_qty) || 0;
  const yieldQty = parseFloat(formula.yield_qty) || 0;
  const yieldRatio = totalInput > 0 ? (yieldQty / totalInput) * 100 : 0;

  // ─── Header ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.rect(marginX, y - 4, 12, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text('SC', marginX + 6, y + 3.5, { align: 'center' });

  doc.setFontSize(11);
  doc.text(COMPANY.name, marginX + 16, y + 1);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(20, 20, 20);
  doc.text('Recipe Sheet', pageWidth / 2, y + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(formula.is_active ? '(Active)' : '(Retired)', pageWidth / 2, y + 7, { align: 'center' });

  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  const rightX = pageWidth - marginX;
  doc.text(`Printed on: ${new Date().toLocaleString('en-GB')}`, rightX, y - 2, { align: 'right' });
  doc.text(COMPANY.address, rightX, y + 2, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}`, rightX, y + 6, { align: 'right' });

  // ─── Divider ───
  y += 12;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, pageWidth - marginX, y);

  // ─── Recipe header row ───
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(26, 60, 110);
  doc.text(`Recipe : ${formula.code}`, marginX, y);
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(9);
  doc.text(`Version : ${formula.version || 1}`, pageWidth / 2, y, { align: 'center' });
  doc.text(`Created : ${formatDate(formula.created_at)}`, rightX, y, { align: 'right' });

  // ─── Bold divider ───
  y += 4;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.6);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 6;

  // ─── Info block ───
  const colW = (pageWidth - marginX * 2) / 2;
  const drawRow = (label, value, x, yPos, valueColor) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(label, x, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...(valueColor || [30, 30, 30]));
    doc.text(String(value || '—'), x + 42, yPos);
  };

  drawRow('Output Product', `${formula.product_code || ''} — ${formula.product_name || ''}`, marginX, y);
  drawRow('Customer', formula.customer_name || 'Generic (no customer)', marginX + colW, y);
  y += 6;

  drawRow('Yield', `${fmtQty(yieldQty)} ${formula.product_unit || ''}`, marginX, y, [39, 174, 96]);
  drawRow('Total Input', `${fmtQty(totalInput)}`, marginX + colW, y);
  y += 6;

  drawRow('Yield Ratio', `${yieldRatio.toFixed(1)}%`, marginX, y, [26, 60, 110]);
  drawRow('Last Updated', formatDate(formula.updated_at || formula.created_at), marginX + colW, y);
  y += 8;

  // ─── Divider before ingredients ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 4;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(26, 60, 110);
  doc.text('Ingredients', marginX, y);
  y += 2;

  // ─── Ingredients table ───
  const rows = (formula.ingredients || []).map((it, i) => {
    const qty = parseFloat(it.quantity) || 0;
    const pctOfInput = totalInput > 0 ? (qty / totalInput) * 100 : 0;
    return [
      String(i + 1),
      it.product_code || '—',
      it.product_name || '—',
      fmtQty(qty),
      it.unit || '—',
      pctOfInput.toFixed(1) + '%',
    ];
  });

  autoTable(doc, {
    startY: y + 2,
    head: [['#', 'Code', 'Raw Material', 'Quantity', 'Unit', '% of Mix']],
    body: rows.length ? rows : [['—', '—', 'No ingredients', '—', '—', '—']],
    foot: [[
      '', '', 'Total',
      fmtQty(totalInput),
      '',
      '100.0%',
    ]],
    theme: 'grid',
    headStyles: {
      fillColor: [232, 232, 232], textColor: [40, 40, 40],
      fontStyle: 'bold', fontSize: 8.5,
      lineColor: [150, 150, 150], lineWidth: 0.2,
    },
    bodyStyles: { fontSize: 8.5, textColor: [40, 40, 40], lineColor: [180, 180, 180], lineWidth: 0.15 },
    footStyles: {
      fillColor: [245, 245, 245], textColor: [40, 40, 40],
      fontStyle: 'bold', fontSize: 8.5,
      lineColor: [150, 150, 150], lineWidth: 0.2,
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) data.cell.styles.textColor = [26, 60, 110];
      if (data.section === 'body' && data.column.index === 5) data.cell.styles.textColor = [39, 174, 96];
    },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 26 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 22, halign: 'center' },
      5: { cellWidth: 26, halign: 'right' },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 6;

  // ─── Notes box ───
  if (formula.notes) {
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    const boxH = 20;
    doc.rect(marginX, afterY, pageWidth - marginX * 2, boxH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text('Instructions / Notes', marginX + 3, afterY + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    const lines = doc.splitTextToSize(formula.notes, pageWidth - marginX * 2 - 6);
    doc.text(lines, marginX + 3, afterY + 11);
    afterY += boxH + 4;
  }

  // ─── Signatures ───
  let sigY = pageHeight - 24;
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.3);
  const sigW = 55;
  const gap = (pageWidth - marginX * 2 - sigW * 3) / 2;
  const sigs = ['Prepared By', 'Verified By', 'Worker'];
  sigs.forEach((label, i) => {
    const sx = marginX + i * (sigW + gap);
    doc.line(sx, sigY, sx + sigW, sigY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(label, sx + sigW / 2, sigY + 5, { align: 'center' });
  });

  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Page 1 of 1', rightX, pageHeight - 6, { align: 'right' });

  doc.save(`Recipe_${(formula.code || 'recipe').replace(/\s+/g, '_')}.pdf`);
};